import axios, {AxiosResponse} from 'axios';
import {
  Account,
  CustomerAccount,
  CreateAccountResponse,
  AccountsQueryResponse,
  MecanizouAccountsQueryResponse,
  InvitedUser,
  GetAccountV2Response,
  CreateProviderAccountRequest,
  AccountUserListItem,
} from '../models';

const API_URL = process.env.REACT_APP_API_URL;
const GET_ACCOUNTS_URL = `${API_URL}/accounts`;
const GET_ACCOUNTS_URL_V2 = `${API_URL}/v2/accounts`;

export const getAccounts = async (
  query?: string
): Promise<{result: AccountsQueryResponse; resultCount: number}> => {
  const response: AxiosResponse<{
    result: AccountsQueryResponse;
    resultCount: number;
  }> = await axios.get(`${GET_ACCOUNTS_URL_V2}?${query}`);

  return response.data;
};

export const getMecanizouAccounts = (
  query?: string
): Promise<MecanizouAccountsQueryResponse> => {
  return axios
    .get(`${GET_ACCOUNTS_URL}/mecanizou${query ? `?${query}` : ''}`)
    .then((d: AxiosResponse<MecanizouAccountsQueryResponse>) => d.data);
};

export const showAccount = (uid: string): Promise<GetAccountV2Response> => {
  return axios
    .get(`${GET_ACCOUNTS_URL_V2}/${uid}`)
    .then((d: AxiosResponse<GetAccountV2Response>) => d.data);
};

export const createAccount = (body: any): Promise<CreateAccountResponse> => {
  return axios
    .post(`${GET_ACCOUNTS_URL_V2}`, body)
    .then((d: AxiosResponse<CreateAccountResponse>) => d.data)
    .catch((e) => {
      throw e;
    });
};

export const inviteAccount = (body: any): Promise<InvitedUser> => {
  return axios
    .post(`${GET_ACCOUNTS_URL}/invite`, body)
    .then((d: AxiosResponse<InvitedUser>) => d.data)
    .catch((e) => {
      throw e;
    });
};

/**
 * Desativa a oficina (a conta `mechanic_shop` ou `customer`) de vez:
 * `DELETE /accounts/:uid` do account-service, só `ROLE_MECANIZOU_ADMIN`. A
 * resposta (o resumo do que foi feito) a tela não usa. Se a oficina tem
 * pedido em andamento ou boleto em aberto, o erro é 422
 * `MECHANIC_SHOP_HAS_OPEN_WORK`; se a conta foi desativada mas as sessões não
 * foram encerradas, 502 `MECHANIC_SHOP_SESSIONS_NOT_REVOKED` (repetir a
 * chamada termina o trabalho). O corpo do erro é
 * `{error: {errorEnum, errorDetails, name, statusCode}}`.
 */
export const deactivateMechanicShop = async (uid: string): Promise<void> => {
  await axios.delete(`${GET_ACCOUNTS_URL}/${uid}`);
};

export const identificateUserAccount = async (): Promise<any> => {
  return await axios
    .get(`${API_URL}/me`)
    .then((d: AxiosResponse<Account>) => d.data)
    .catch((e) => {
      throw e;
    });
};

export const updateAccount = (uid: string, body: any): Promise<Account> => {
  return axios
    .put(`${GET_ACCOUNTS_URL_V2}/${uid}`, body)
    .then((d: AxiosResponse<Account>) => d.data)
    .catch((e) => {
      throw e;
    });
};

export const changePassword = async (changePasswordBody: any): Promise<any> => {
  try {
    return await axios.put(
      `${GET_ACCOUNTS_URL}/change-password`,
      changePasswordBody
    );
  } catch (error) {
    throw error;
  }
};

export const getClientAccountUsersByAccountUid = (
  accountUid: string,
  query?: string
): Promise<CustomerAccount[]> => {
  return axios
    .get(`${GET_ACCOUNTS_URL}/${accountUid}/users?${query}`)
    .then((d: AxiosResponse<CustomerAccount[]>) => d.data)
    .catch((e) => {
      throw e;
    });
};

export const createProviderAccount = (
  body: CreateProviderAccountRequest
): Promise<{message: string}> => {
  return axios
    .post(`${GET_ACCOUNTS_URL}/providers`, body)
    .then((d: AxiosResponse<{message: string}>) => d.data);
};

/**
 * Vínculos ativos de uma conta (Mecanizou lê qualquer conta). Na ativação do
 * fornecedor, a equipe das contas de loja que já existem: a porta Ops do PHS
 * só lista a equipe de loja que já está numa rede.
 */
export const listAccountUsers = (
  accountUid: string
): Promise<AccountUserListItem[]> =>
  axios
    .get(`${GET_ACCOUNTS_URL}/${accountUid}/users`)
    .then((d: AxiosResponse<AccountUserListItem[]>) =>
      Array.isArray(d.data) ? d.data : []
    );
